// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file Banking.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Hub principal de serviços bancários
// @date 2026-01-28
// ------------------------------------------------------------

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { bankingService } from '../services';
import { useToast } from '../contexts/ToastContext';
import { Card, LoaderComponent } from '../components/ui';
import type {
  BankAccount,
  AggregatedBalance,
  Transfer,
  FinancialHealthScore,
  Budget,
  SavingsGoal,
  CreditContract,
  SecurityAlert,
} from '../types';
import {
  Building2,
  ArrowRightLeft,
  CreditCard,
  PiggyBank,
  Receipt,
  Shield,
  TrendingUp,
  AlertTriangle,
  ChevronRight,
  Plus,
  Wallet,
  Target,
  BarChart3,
  QrCode,
  Send,
  Download,
  History,
} from 'lucide-react';

interface QuickAction {
  icon: React.ReactNode;
  label: string;
  description: string;
  path: string;
  color: string;
}

const Banking: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [aggregatedBalance, setAggregatedBalance] = useState<AggregatedBalance | null>(null);
  const [recentTransfers, setRecentTransfers] = useState<Transfer[]>([]);
  const [healthScore, setHealthScore] = useState<FinancialHealthScore | null>(null);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [goals, setGoals] = useState<SavingsGoal[]>([]);
  const [contracts, setContracts] = useState<CreditContract[]>([]);
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);

  const quickActions: QuickAction[] = [
    {
      icon: <Send size={24} />,
      label: t('banking.transferLabel'),
      description: t('banking.p2p'),
      path: '/banking/transfer',
      color: '#2196F3',
    },
    {
      icon: <Receipt size={24} />,
      label: t('banking.payServices'),
      description: t('banking.payServicesDesc'),
      path: '/banking/bills',
      color: '#4CAF50',
    },
    {
      icon: <QrCode size={24} />,
      label: t('banking.qrCode'),
      description: t('banking.qrCodeDesc'),
      path: '/banking/qr',
      color: '#9C27B0',
    },
    {
      icon: <CreditCard size={24} />,
      label: t('banking.credit'),
      description: t('banking.creditDesc'),
      path: '/banking/credit',
      color: '#FF9800',
    },
  ];

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Banking');
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load all data in parallel
      const [
        accountsData,
        balanceData,
        transfersData,
        healthData,
        budgetsData,
        goalsData,
        contractsData,
        alertsData,
      ] = await Promise.allSettled([
        bankingService.getAccounts(),
        bankingService.getAggregatedBalance(),
        bankingService.getTransferHistory({ startDate: getDateDaysAgo(30) }),
        bankingService.getFinancialHealthScore(),
        bankingService.getBudgets(),
        bankingService.getSavingsGoals(),
        bankingService.getCreditContracts(),
        bankingService.getSecurityAlerts(),
      ]);

      if (accountsData.status === 'fulfilled') setAccounts(accountsData.value);
      if (balanceData.status === 'fulfilled') setAggregatedBalance(balanceData.value);
      if (transfersData.status === 'fulfilled') setRecentTransfers(transfersData.value.slice(0, 5));
      if (healthData.status === 'fulfilled') setHealthScore(healthData.value);
      if (budgetsData.status === 'fulfilled') setBudgets(budgetsData.value);
      if (goalsData.status === 'fulfilled') setGoals(goalsData.value);
      if (contractsData.status === 'fulfilled') setContracts(contractsData.value);
      if (alertsData.status === 'fulfilled') setAlerts(alertsData.value.filter((a) => !a.acknowledged));
    } catch (err) {
      showError(t('banking.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const getDateDaysAgo = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
  };

  const formatAmount = (amount: number | undefined | null): string => {
    const value = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    return new Intl.NumberFormat('pt-CV', {
      style: 'currency',
      currency: 'CVE',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('pt-CV', {
      timeZone: 'Atlantic/Cape_Verde',
      day: '2-digit',
      month: 'short',
    });
  };

  const getScoreColor = (grade: string): string => {
    const colors: Record<string, string> = {
      A: '#4CAF50',
      B: '#8BC34A',
      C: '#FFC107',
      D: '#FF9800',
      F: '#F44336',
    };
    return colors[grade] || '#9E9E9E';
  };

  const getBudgetStatus = (budget: Budget): { color: string; label: string } => {
    const percentage = (budget.spent / budget.amount) * 100;
    if (percentage >= 100) return { color: '#F44336', label: t('banking.exceeded') };
    if (percentage >= 80) return { color: '#FF9800', label: t('banking.attention') };
    return { color: '#4CAF50', label: t('banking.ok') };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoaderComponent />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Security Alerts */}
      {alerts.length > 0 && (
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-center gap-3 p-4">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle size={24} className="text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">{t('banking.securityAlerts')}</h3>
              <p className="text-sm text-red-600">{t('banking.pendingAlerts', { count: alerts.length })}</p>
            </div>
            <button
              onClick={() => navigate('/banking/security')}
              className="text-red-600 hover:text-red-800"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </Card>
      )}

      {/* Balance Card */}
      <Card className="bg-gradient-to-r from-blue-600 to-blue-800 text-white overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wallet size={20} />
              <span className="text-blue-100">{t('banking.totalBalance')}</span>
            </div>
            <span className="px-2 py-1 text-xs bg-white/20 rounded-full">
              {accounts.length} {t('banking.accounts')}
            </span>
          </div>
          <div className="text-3xl font-bold mb-6">
            {aggregatedBalance ? formatAmount(aggregatedBalance.totalBalance) : formatAmount(0)}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/banking/transfer')}
              className="flex items-center justify-center gap-2 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <Send size={18} />
              <span>{t('banking.send')}</span>
            </button>
            <button
              onClick={() => navigate('/banking/accounts')}
              className="flex items-center justify-center gap-2 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <Download size={18} />
              <span>{t('banking.receive')}</span>
            </button>
          </div>
        </div>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {quickActions.map((action, index) => (
          <Card
            key={index}
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate(action.path)}
          >
            <div className="p-4 text-center">
              <div
                className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${action.color}20` }}
              >
                <div style={{ color: action.color }}>{action.icon}</div>
              </div>
              <h3 className="font-semibold text-gray-800">{action.label}</h3>
              <p className="text-xs text-gray-500 mt-1">{action.description}</p>
            </div>
          </Card>
        ))}
      </div>

      {/* Connected Accounts */}
      <Card>
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 size={20} className="text-blue-600" />
            <h2 className="font-semibold">{t('banking.connectedAccounts')}</h2>
          </div>
          <button
            onClick={() => navigate('/banking/accounts/connect')}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            <Plus size={16} />
            {t('common.add')}
          </button>
        </div>
        <div className="divide-y">
          {accounts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Building2 size={48} className="mx-auto mb-3 text-gray-300" />
              <p>{t('banking.noConnectedAccounts')}</p>
              <button
                onClick={() => navigate('/banking/accounts/connect')}
                className="mt-3 text-blue-600 hover:text-blue-800"
              >
                {t('banking.connectBankAccount')}
              </button>
            </div>
          ) : (
            accounts.map((account) => (
              <div
                key={account.id}
                className="p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/banking/accounts/${account.id}`)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <Building2 size={20} className="text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium">{account.bankName}</p>
                    <p className="text-sm text-gray-500">
                      {account.accountType === 'checking' ? t('banking.checkingAccount') : t('banking.savingsAccount')} •{' '}
                      ****{account.accountNumber.slice(-4)}
                    </p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Financial Health Score */}
      {healthScore && (
        <Card>
          <div className="p-4 border-b flex items-center gap-2">
            <TrendingUp size={20} className="text-green-600" />
            <h2 className="font-semibold">{t('banking.financialHealth')}</h2>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-4">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                style={{ backgroundColor: getScoreColor(healthScore.grade) }}
              >
                {healthScore.grade}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">{t('banking.score')}</span>
                  <span className="font-semibold">
                    {healthScore.score}/{healthScore.maxScore}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full"
                    style={{
                      width: `${(healthScore.score / healthScore.maxScore) * 100}%`,
                      backgroundColor: getScoreColor(healthScore.grade),
                    }}
                  />
                </div>
              </div>
            </div>
            {healthScore.recommendations && healthScore.recommendations.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2">{t('banking.recommendations')}:</p>
                <ul className="space-y-1">
                  {healthScore.recommendations.slice(0, 2).map((rec, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-green-600">•</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Budgets & Goals Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Budgets */}
        <Card>
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 size={20} className="text-purple-600" />
              <h2 className="font-semibold">{t('banking.budgets')}</h2>
            </div>
            <button
              onClick={() => navigate('/banking/budgets')}
              className="text-sm text-purple-600 hover:text-purple-800"
            >
              {t('common.viewAll')}
            </button>
          </div>
          <div className="p-4 space-y-4">
            {budgets.length === 0 ? (
              <div className="text-center text-gray-500 py-4">
                <BarChart3 size={32} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm">{t('banking.noBudgets')}</p>
              </div>
            ) : (
              budgets.slice(0, 3).map((budget) => {
                const status = getBudgetStatus(budget);
                const percentage = Math.min((budget.spent / budget.amount) * 100, 100);
                return (
                  <div key={budget.id}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{budget.categoryName}</span>
                      <span className="text-xs" style={{ color: status.color }}>
                        {status.label}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                      <div
                        className="h-2 rounded-full"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: status.color,
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{formatAmount(budget.spent)}</span>
                      <span>{formatAmount(budget.amount)}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        {/* Savings Goals */}
        <Card>
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target size={20} className="text-green-600" />
              <h2 className="font-semibold">{t('banking.savingsGoals')}</h2>
            </div>
            <button
              onClick={() => navigate('/banking/goals')}
              className="text-sm text-green-600 hover:text-green-800"
            >
              {t('common.viewAllFemale')}
            </button>
          </div>
          <div className="p-4 space-y-4">
            {goals.length === 0 ? (
              <div className="text-center text-gray-500 py-4">
                <PiggyBank size={32} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm">{t('banking.noGoals')}</p>
              </div>
            ) : (
              goals.slice(0, 3).map((goal) => (
                <div key={goal.id}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{goal.name}</span>
                    <span className="text-xs text-gray-500">{goal.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                    <div
                      className="h-2 rounded-full bg-green-500"
                      style={{ width: `${goal.progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{formatAmount(goal.currentAmount)}</span>
                    <span>{formatAmount(goal.targetAmount)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Active Credits */}
      {contracts.length > 0 && (
        <Card>
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard size={20} className="text-orange-600" />
              <h2 className="font-semibold">{t('banking.activeCredits')}</h2>
            </div>
            <button
              onClick={() => navigate('/banking/credit')}
              className="text-sm text-orange-600 hover:text-orange-800"
            >
              {t('common.viewAll')}
            </button>
          </div>
          <div className="divide-y">
            {contracts.slice(0, 2).map((contract) => (
              <div
                key={contract.id}
                className="p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/banking/credit/${contract.id}`)}
              >
                <div>
                  <p className="font-medium">{contract.productName}</p>
                  <p className="text-sm text-gray-500">
                    {contract.remainingInstallments} {t('banking.remainingInstallments')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{formatAmount(contract.monthlyPayment)}</p>
                  <p className="text-xs text-gray-500">
                    {t('banking.nextPayment')}: {formatDate(contract.nextPaymentDate)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recent Transfers */}
      <Card>
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History size={20} className="text-gray-600" />
            <h2 className="font-semibold">{t('banking.recentTransfers')}</h2>
          </div>
          <button
            onClick={() => navigate('/banking/history')}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {t('banking.viewHistory')}
          </button>
        </div>
        <div className="divide-y">
          {recentTransfers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <ArrowRightLeft size={48} className="mx-auto mb-3 text-gray-300" />
              <p>{t('banking.noRecentTransfers')}</p>
            </div>
          ) : (
            recentTransfers.map((transfer) => (
              <div key={transfer.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      transfer.type === 'p2p'
                        ? 'bg-blue-100'
                        : transfer.type === 'bank'
                          ? 'bg-green-100'
                          : 'bg-purple-100'
                    }`}
                  >
                    <Send
                      size={18}
                      className={
                        transfer.type === 'p2p'
                          ? 'text-blue-600'
                          : transfer.type === 'bank'
                            ? 'text-green-600'
                            : 'text-purple-600'
                      }
                    />
                  </div>
                  <div>
                    <p className="font-medium">{transfer.recipientName}</p>
                    <p className="text-sm text-gray-500">
                      {transfer.type === 'p2p'
                        ? t('banking.pagali')
                        : transfer.type === 'bank'
                          ? t('banking.bankTransfer')
                          : t('banking.international')}{' '}
                      • {formatDate(transfer.createdAt)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-red-600">-{formatAmount(transfer.amount)}</p>
                  <p
                    className={`text-xs ${
                      transfer.status === 'completed'
                        ? 'text-green-600'
                        : transfer.status === 'pending'
                          ? 'text-yellow-600'
                          : 'text-red-600'
                    }`}
                  >
                    {transfer.status === 'completed'
                      ? t('transactions.completed')
                      : transfer.status === 'pending'
                        ? t('payments.pending')
                        : t('payments.failed')}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
};

export default Banking;
